import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { ProgressTabs } from '@/components/ProgressTabs';
import { toast } from '@/components/toast';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import { VALIDATE_USER_CAPPING_QUERY_KEY } from '@/pages/avatar';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import { HeaderButtons } from '@/pages/avatar/CreatePage/components/Header/HeaderButtons';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { GET_PRESENTERS_KEY, GET_PRESENTERS_KEY_TYPE } from '@/state/presenters/presenters';
import theme from '@/theme';
import { AppPathname, AppRouteNames, ExtendedLocation, MixPanelEvent } from '@/utils/types';
import { AvatarStatus, IExpressPresenter, IPresenter, ImageType, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { CircularProgress, IconButton, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { nanoid } from 'nanoid';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { LeaveConfirmationModal } from '../../../../../components/LeaveConfirmationModal';
import { CreateAvatarStore } from '../../CreateAvatarStore';
import { saveDraftAvatar } from './saveDraftAvatar';

const HeaderContainer = styled.header`
    width: 100%;
    display: flex;
    background: ${theme.colors.white};
    align-items: center;
    justify-content: space-between;
    border-bottom: ${theme.colors.black[200]} solid 1px;
    min-height: 96px;
    padding: 0 32px;
    position: relative;
    box-shadow: 0px 1px 2px 0px rgba(187, 187, 187, 0.12);
`;

const StyledBackButton = styled(IconButton)`
    gap: 10px;

    &:active {
        background-color: ${({ theme }) => theme.colors.black[50]};
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const StyledMaskedIcon = styled(MaskedIcon)`
    color: ${({ theme }) => theme.colors.black[700]};
`;

const BackText = styled(Typography)`
    color: ${({ theme }) => theme.colors.black[700]};
    font-weight: 500;
`;

const StyledProgressTabs = styled(ProgressTabs)`
    position: absolute;
    width: 30%;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-direction: column;
    margin-top: 8px;
    left: 50%;
    transform: translate(-50%, 0);
`;

export const Header = observer(() => {
    const navigate = useNavigate();
    const location = useLocation() as ExtendedLocation;

    const createAvatarStore = useCreateAvatarStore() as CreateAvatarStore;
    const [confirmationModal, setConfirmationModal] = useState(false);
    const mixpanel = useAnalytics();
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const handleBack = () => {
        if (createAvatarStore.isDirty) {
            return setConfirmationModal(true);
        }

        handleLeave();
    };

    const mixpanelPayload = {
        type: 'express',
        source: createAvatarStore.footageMethod,
        plan: user?.planGroup,
        presenterId: createAvatarStore.id,
        fromDraft: !!createAvatarStore.id,
        isScript: createAvatarStore.usedFootageScript,
        script_language: createAvatarStore.footageScriptLanguage,
        consent_language: createAvatarStore.consentLanguage,
    };

    const mixpanelKey = `avatar-express-${createAvatarStore.id ? 'edit' : 'create'}-draft`;

    const saveMutation = useMutation(
        () => {
            mixpanel?.track(mixpanelKey, { ...mixpanelPayload, event: MixPanelEvent.CLICK });
            return saveDraftAvatar();
        },
        {
            onMutate: async () => {
                const previousData = queryClient.getQueryData([GET_PRESENTERS_KEY, GET_PRESENTERS_KEY_TYPE(ImageType.Upload), 'all']) || [];

                const newAvatar: IExpressPresenter = {
                    source_url: 'temp',
                    consent_id: 'temp',
                    status: AvatarStatus.Uploading,
                    source_duration: 0,
                    previewUrl: '',
                    id: nanoid(),
                    created_at: new Date().toISOString(),
                    name: 'Untitled avatar',
                    thumbnail: createAvatarStore.thumbnailSrc,
                    poster: '',
                    url: '',
                    order: 0,
                    isDefault: false,
                    type: VideoType.Scene,
                    created_by: '',
                    is_green_screen: false,
                };

                queryClient.setQueryData(
                    [GET_PRESENTERS_KEY, GET_PRESENTERS_KEY_TYPE(ImageType.Upload), 'all'],
                    [newAvatar, ...(previousData as IPresenter[])]
                );

                return { previousData };
            },
            onSuccess: async ({ id }) => {
                mixpanel?.track(mixpanelKey, { ...mixpanelPayload, id, event: MixPanelEvent.SUCCESS });
                await queryClient.invalidateQueries(VALIDATE_USER_CAPPING_QUERY_KEY, { refetchInactive: true });
                toast.success({
                    title: 'Express avatar draft saved successfully',
                });
            },
            onError: error => {
                mixpanel?.track(mixpanelKey, { ...mixpanelPayload, event: MixPanelEvent.ERROR });
                toast.error({
                    title: 'Failed to save draft avatar, please try again',
                });
                logger.error('Failed to save draft avatar', error);
            },
            onSettled: async () => {
                await queryClient.invalidateQueries([GET_PRESENTERS_KEY, GET_PRESENTERS_KEY_TYPE(ImageType.Upload), 'all']);
            },
        }
    );

    const handleLeave = () => {
        setConfirmationModal(false);

        // TODO 9/2/2025 omert: remove when can redirect to editor, currently we have a behaviour do delete empty projects that cause
        // a bug with getting back to the same project in the editor
        // Get the previous pathname from history state
        const previousPath = location.state?.fromPath || document.referrer || '';
        // If the previous path contains "editor", navigate to home instead
        if (previousPath.includes('editor')) {
            navigate('/');
        } else {
            navigate(-1);
        }
    };

    const handleCancel = () => {
        createAvatarStore.resetUploads();
        handleLeave();
    };

    const onSave = () => {
        saveMutation.mutate();
        handleLeave();
    };

    // should only be null if someone forgets to set the fromPath state
    const prevPageName = location.state?.fromPath ? AppRouteNames[location.state.fromPath as AppPathname] : '';

    return (
        <>
            <HeaderContainer>
                <StyledBackButton onClick={handleBack} disabled={saveMutation.isLoading}>
                    {saveMutation.isLoading && <CircularProgress size={24} />}
                    {!saveMutation.isLoading && (
                        <>
                            <StyledMaskedIcon src={ArrowLeft} size="32px" />
                            <BackText variant="h4">{prevPageName}</BackText>
                        </>
                    )}
                </StyledBackButton>
                <StyledProgressTabs
                    tab={createAvatarStore.tab}
                    setTab={(tab: number) => createAvatarStore.setTab(tab)}
                    tabs={createAvatarStore.tabs}
                />
                <HeaderButtons />
            </HeaderContainer>
            <LeaveConfirmationModal
                onClose={() => setConfirmationModal(false)}
                onConfirm={onSave}
                onCancel={handleCancel}
                open={confirmationModal}
            />
        </>
    );
});
